/** Pure helpers for the global search palette — kept free of Angular so they unit-test in isolation. */

/** Case- and accent-insensitive comparison key ("Équipe" matches "equipe"). */
export function normalizeForMatch(text: string): string {
  return (text || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
}

/**
 * Filter `items` by name against `query`, best matches first: names that START
 * with the query outrank names that merely contain it; ties keep input order.
 */
export function rankByName<T>(items: readonly T[], query: string, nameOf: (item: T) => string, limit = 6): T[] {
  const q = normalizeForMatch(query);
  if (!q) return [];
  const scored: { item: T; score: number; index: number }[] = [];
  items.forEach((item, index) => {
    const name = normalizeForMatch(nameOf(item));
    if (!name) return;
    if (name.startsWith(q)) scored.push({ item, score: 0, index });
    else if (name.includes(q)) scored.push({ item, score: 1, index });
  });
  return scored
    .sort((a, b) => a.score - b.score || a.index - b.index)
    .slice(0, limit)
    .map((s) => s.item);
}

export interface MessageHitLike {
  source?: 'connect' | 'chat' | string;
  conversationId: string;
  chatId?: string;
  messageId: string;
}

export interface MessageRouteContext {
  /** Team spaces (every team doc, incl. former `bt-*` teams) — they live under /messaging. */
  teamConversationIds: ReadonlySet<string>;
  /** Org spaces (retired from the rail, still reachable) — also /messaging. */
  spaceConversationIds: ReadonlySet<string>;
}

export interface MessageRouteTarget {
  commands: string[];
  queryParams: Record<string, string>;
}

/**
 * Where a message hit opens. Chat hits open the chat; connect hits open the
 * conversation on the page that owns it (team spaces and org spaces under
 * /messaging, everything else — agent DMs, shared agents, anything unknown —
 * under /agents), carrying `messageId` so the transcript scrolls to it.
 */
export function resolveMessageRoute(hit: MessageHitLike, context: MessageRouteContext): MessageRouteTarget {
  if (hit.source === 'chat') {
    return { commands: ['/chat/c', hit.chatId || hit.conversationId], queryParams: {} };
  }
  const queryParams = { conversationId: hit.conversationId, messageId: hit.messageId };
  if (context.teamConversationIds.has(hit.conversationId) || context.spaceConversationIds.has(hit.conversationId)) {
    return { commands: ['/messaging'], queryParams };
  }
  return { commands: ['/agents'], queryParams };
}

/** Same relative time the old result rows showed: time today, date otherwise. */
export function formatHitTime(createdOn: number | undefined, now: Date = new Date()): string {
  if (!createdOn) return '';
  const date = new Date(createdOn);
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}
